import { createSelector } from '@reduxjs/toolkit';
import { Employee } from 'api/client-domain/client-domain.types';
import { createCachedSelector, FlatMapCache } from 're-reselect';
import {
  hasAllPermissions,
  hasAnyPermission,
  hasPermission,
} from 'util/authorization';

export const selectUser = state => state.user;

export const selectAccessToken = createSelector(
  selectUser,
  user => user.access_token,
);

export const selectRefreshToken = createSelector(
  selectUser,
  user => user.refresh_token,
);

export const selectClientId = createSelector(
  selectUser,
  user => user.client_id,
);

export const selectAdminId = createSelector(
  selectUser,
  user => user.account_id,
);

export const selectUserDetails = createSelector(
  selectUser,
  user => user.userDetails,
);

export const selectStatsigUser = createSelector(selectUser, user =>
  !!user.access_token
    ? {
        userID: user.account_id,
        email: user.userDetails?.email,
        custom: {
          clientId: user.client_id,
        },
      }
    : {
        custom: {
          clientId: user.client_id,
        },
      },
);

export const selectIsAuthenticated = createSelector(
  selectAccessToken,
  accessToken => !!accessToken,
);

export const selectEmail = createSelector(
  selectUserDetails,
  userDetails => userDetails?.email,
);

export const selectIsFullyAuthenticated = createSelector(
  selectAccessToken,
  selectUserDetails,
  (accessToken, userDetails) => !!accessToken && !!userDetails,
);

export const selectAllPermissions = createSelector(
  selectUserDetails,
  (userDetails: Employee) =>
    userDetails?.role?.modules
      ?.filter(module => !!module.active && !module.deleted)
      .flatMap(module => module.roleModulePermissions)
      .filter(permission => !!permission.active && !permission.deleted),
);

const permissionPropSelector = (_, permission) => permission;

/**
 * All right let's talk about re-resleect and createCachedSelector. We're not
 * using plain old createSelector because we want to pass an argument to our
 * selector as well. In this case it's the permission or permissions prop. What
 * we're trying to do is say "is this permission or some of these permissions
 * part of all of the User's permissions"? The selector should return true or
 * false.
 *
 * We actually can do this with plain old reselect and createSelector by doing
 * something like
 *
 *  export const createSelectHasPermission = (permission) => createSelector(
      selectAllPermissions, (allPermissions, permission) =>
      !!findPermission(permission, allPermissions),
    );

 * The problem here is that that createSelector is executed every single time we
 * call this, which invalidates the cache, which is the entire reason we're
 * using reselect. See
 * https://react-redux.js.org/api/hooks#using-memoizing-selectors.
 *
 * Ok, so a solution we've used in the past is combining something like the
 * above with useCallback or useMemo to add an 'extra layer' of caching. This
 * WORKS, but then we start writing a bunch of hooks and honestly the logic gets
 * real tough. If you worked on web-ordering-v2 then you know that order
 * creation + address stuff was a real problem.
 *
 * So, instead of that we use re-reslect. It is built exactly for this problem.
 * It ends up being ALMOST as complex as normal selectors + useCallback, but at
 * least we're keeping all of the logic in once place. When we start spreading
 * stuff across hooks, it gets difficult to understand how and when things
 * interact. I think that's worse.
 *
 * See https://github.com/toomuchdesign/re-reselect
 *
 * So the code below WORKS, but some of it is kind of hard to understand. This
 * can be seen in selectHasAnyPermissions or selectHasAllPermissions. What is
 * this FlatMapCache thing? The oversimplified explanation is re-reselect and
 * createCachedSelector expects a string for a cache key. As a result we have to
 * provide a custom cacheObject. So we provid a custom cacheObject built for
 * arrays.
 *
 * What does that mean? Honestly I'm not 100% sure. If you want to find out for
 * yourself you will have to go through the re-reselect source code, write unit
 * tests, debug and figure out wth is going on. I understand about 80% of this
 * library and this is the result.
 */
export const selectHasPermission = createCachedSelector(
  selectAllPermissions,
  permissionPropSelector,
  hasPermission,
)((_state, permission) => permission ?? '');

export const selectHasAnyPermissions = createCachedSelector(
  selectAllPermissions,
  permissionPropSelector,
  hasAnyPermission,
)({
  keySelector: permissionPropSelector,
  cacheObject: new FlatMapCache(),
});

export const selectHasAllPermissions = createCachedSelector(
  selectAllPermissions,
  permissionPropSelector,
  hasAllPermissions,
)({
  keySelector: permissionPropSelector,
  cacheObject: new FlatMapCache(),
});

export const selectUserRole = createSelector(
  selectUserDetails,
  userDetails => userDetails?.role?.role,
);
